import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
// Auth is part of the app shell; the root barrel eagerly initializes every
// feature schema before the first page. Keep this boundary limited to auth.
import { LoginResponse, MeResponse, LogoutResponse, ExitImpersonationResponse } from '@yrp/shared/auth';
import type { LoginRequest } from '@yrp/shared/auth';
import { apiFetch } from '../../lib/api.js';
import { env } from '../../lib/env.js';

const ME_KEY = ['auth', 'me'] as const;

const LOGOUT_LOCAL_STORAGE_KEYS = [
  'yrp.mls.appliedFilters.v1',
  'yrp.mls.appliedFilters.v2',
] as const;

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const { user } = await apiFetch('/api/auth/me', MeResponse);
        return user;
      } catch (err) {
        // 401 is the "not logged in" happy path, not an error state
        if ((err as { statusCode?: number }).statusCode === 401) return null;
        throw err;
      }
    },
  });
}

const IMPERSONATION_KEY = ['auth', 'impersonating'] as const;

/**
 * Returns the impersonation context when the current session was
 * opened by an admin via /admin/users/:id/impersonate, otherwise null.
 * The impersonated user always sees null — they aren't impersonating
 * anyone, even though their session was opened by an admin.
 */
export function useImpersonation() {
  return useQuery({
    queryKey: IMPERSONATION_KEY,
    queryFn: async () => {
      try {
        const { impersonating } = await apiFetch('/api/auth/me', MeResponse);
        return impersonating;
      } catch (err) {
        if ((err as { statusCode?: number }).statusCode === 401) return null;
        throw err;
      }
    },
  });
}

export function useExitImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/api/auth/exit-impersonation', ExitImpersonationResponse, { method: 'POST' }),
    onSuccess: () => {
      // Same logic as login: wipe caches scoped to the impersonated
      // session, then let /auth/me re-fetch under the admin cookie.
      qc.clear();
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LoginRequest) => {
      const res = await apiFetch('/api/auth/login', LoginResponse, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return res;
    },
    onSuccess: (res) => {
      qc.setQueryData(ME_KEY, res.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch('/api/auth/logout', LogoutResponse, { method: 'POST' }),
    onSuccess: () => {
      for (const key of LOGOUT_LOCAL_STORAGE_KEYS) {
        window.localStorage.removeItem(key);
      }
      qc.setQueryData(ME_KEY, null);
      qc.invalidateQueries({ queryKey: ME_KEY });
      // Hard navigation to /login so the route's beforeLoad guard re-runs
      // with the cleared session — TanStack Router's lazy auth check
      // doesn't fire from a useQuery invalidation alone, which is why
      // before this the previous page stayed rendered until a manual
      // refresh.
      window.location.assign('/login');
    },
  });
}

const OkResponse = z.object({ ok: z.literal(true) });

/**
 * Upload a profile photo for the signed-in user. Multipart — bypasses
 * apiFetch (JSON-only) and uses raw fetch with session cookies.
 * Server enforces image/* mime + 5 MB cap.
 */
export function useUploadProfilePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${env.VITE_API_URL}/api/auth/me/profile-photo`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { message?: string }).message ?? `HTTP ${res.status}`;
        throw new Error(msg);
      }
      return MeResponse.parse(await res.json());
    },
    onSuccess: (res) => {
      qc.setQueryData(ME_KEY, res.user);
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useDeleteProfilePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/api/auth/me/profile-photo', OkResponse, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}
